import { TempNode } from 'three/webgpu'
import { nodeObject, Fn, uv, uniform, vec2, sin, cos, vec4, convertToTexture, length, pow } from 'three/tsl'

/**
 * Custom radial RGB shift effect with distance-based falloff
 */
class RadialRGBShiftNode extends TempNode {
  static get type() {
    return 'RadialRGBShiftNode'
  }

  constructor(textureNode, amount = 0.005, angle = 0, radialIntensity = 1.0) {
    super('vec4')
    
    this.textureNode = textureNode
    this.amount = uniform(amount)
    this.angle = uniform(angle)
    this.radialIntensity = uniform(radialIntensity)
  }

  setup() {
    const { textureNode } = this
    const uvNode = textureNode.uvNode || uv()
    
    const sampleTexture = (uv) => textureNode.sample(uv)
    
    const radialRGBShift = Fn(() => {
      // Calculate distance from center (0.5, 0.5)
      const uvCentered = uvNode.sub(vec2(0.5, 0.5))
      const distFromCenter = length(uvCentered).mul(2.0) // Normalize so edges reach ~1.41
      
      // Apply power curve to control falloff
      const radialMask = pow(distFromCenter, this.radialIntensity)
      
      // Calculate offset based on radial distance
      const effectiveAmount = this.amount.mul(radialMask)
      const offset = vec2(cos(this.angle), sin(this.angle)).mul(effectiveAmount)
      
      // Sample RGB channels with offset
      const cr = sampleTexture(uvNode.add(offset))
      const cga = sampleTexture(uvNode)
      const cb = sampleTexture(uvNode.sub(offset))
      
      return vec4(cr.r, cga.g, cb.b, cga.a)
    })
    
    return radialRGBShift()
  }
}

export default RadialRGBShiftNode

export const radialRGBShift = (node, amount, angle, radialIntensity) =>
  nodeObject(new RadialRGBShiftNode(convertToTexture(node), amount, angle, radialIntensity))
